import { useState } from "react";
import { MapPin } from "lucide-react";
import PageTransition from "../components/PageTransition";
import PageHero from "../components/PageHero";
import ImagePlaceholder from "../components/ImagePlaceholder";
import { projects, projectFilters } from "../data/projects";
import { useTranslation } from "../i18n/LanguageContext";
import type { Project } from "../data/projects";

function ProjectModal({ project, onClose }: { project: Project; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/30" />
      <div onClick={(e) => e.stopPropagation()} className="relative z-10 bg-white max-w-lg w-full max-h-[85vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 z-20 w-8 h-8 flex items-center justify-center text-secondary hover:text-primary transition-colors" aria-label="Fermer">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" /></svg>
        </button>
        <ImagePlaceholder label={project.imageLabel} src={project.imageSrc} aspectRatio="16/10" />
        <div className="p-8">
          <span className="text-[12px] uppercase tracking-[0.1em] font-semibold text-accent">{project.category}</span>
          <h3 className="font-serif text-2xl mt-2 mb-4">{project.title}</h3>
          <p className="text-secondary text-[15px] leading-relaxed mb-6">{project.description}</p>
          <div className="flex items-center gap-2 text-secondary text-[14px] border-t border-border pt-5">
            <MapPin size={16} className="text-accent flex-shrink-0" />
            <span>{project.location}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Realisations() {
  const [filter, setFilter] = useState<string>("Tous");
  const [selected, setSelected] = useState<Project | null>(null);
  const t = useTranslation();

  const filtered = filter === "Tous" ? projects : projects.filter((p) => p.category === filter);
  const filterLabels: Record<string, string> = {
    Tous: t.real_filter_all, Construction: t.svc_construction_title,
    "Travaux Publics": t.svc_tp_title, Logistique: t.svc_logistique_title,
  };

  return (
    <PageTransition>
      <PageHero title={t.real_title} description={t.real_description} />

      <section className="py-24 md:py-32">
        <div className="max-w-content mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-6 mb-16">
            {projectFilters.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`relative text-[13px] uppercase tracking-[0.08em] font-medium pb-2 transition-colors duration-300 ${
                  filter === f ? "text-primary" : "text-secondary hover:text-primary"
                }`}
              >
                {filterLabels[f] || f}
                <span className={`absolute left-0 bottom-0 h-[2px] bg-accent transition-all duration-300 ${filter === f ? "w-full" : "w-0"}`} />
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {filtered.map((p) => (
              <button key={p.id} onClick={() => setSelected(p)} className="text-left group">
                <div className="overflow-hidden mb-5">
                  <ImagePlaceholder
                    label={p.imageLabel}
                    src={p.imageSrc}
                    aspectRatio="16/10"
                    className="group-hover:scale-[1.03] transition-transform duration-[600ms]"
                  />
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-[12px] uppercase tracking-[0.1em] font-semibold text-accent">{p.category}</span>
                </div>
                <h3 className="font-serif text-xl group-hover:text-accent transition-colors duration-300 mb-2">{p.title}</h3>
                <div className="flex items-center gap-2 text-secondary text-[14px]">
                  <MapPin size={14} className="text-accent flex-shrink-0" />
                  <span>{p.location}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {selected && <ProjectModal project={selected} onClose={() => setSelected(null)} />}
    </PageTransition>
  );
}
